"use client";

import { useMemo, useState } from "react";
import { ChordLibraryCard } from "@/components/ui/molecules/chord-library-card";
import {
  diatonicChordsForKey,
  ESSENTIAL_CATALOG,
  FULL_CATALOG,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { noteLabel } from "@/lib/music/notation";
import { useTonality } from "@/contexts/studio-context";
import type { Tonality } from "@/lib/music/tonality";

// Liste compacte par défaut : un accord par tonique (majeur). Les variantes
// (mineur, dim, sus, etc.) restent accessibles depuis la rangée de variantes
// du ChordPracticeGrid une fois un accord sélectionné.
const DEFAULT_CATALOG: ChordEntry[] = ESSENTIAL_CATALOG.filter(
  (e) => e.quality === "major",
);

const VIOLET_BRIGHT = "#c4b5fd";
const VIOLET_SOFT = "rgba(139, 92, 246, 0.12)";
const TEXT = "rgba(255, 255, 255, 0.92)";
const TEXT_DIM = "rgba(255, 255, 255, 0.55)";
const TEXT_MUTE = "rgba(255, 255, 255, 0.32)";
const BORDER = "rgba(255, 255, 255, 0.06)";
const SURFACE_LO = "rgba(255, 255, 255, 0.02)";
const FONT_MONO =
  "var(--font-mono), 'JetBrains Mono', ui-monospace, monospace";

type Props = {
  onSelect: (entry: ChordEntry) => void;
};

type DisplayEntry = {
  entry: ChordEntry;
  roman?: string;
};

export function ChordsOverlayList({ onSelect }: Props) {
  const [tonality] = useTonality();
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");

  const diatonic = useMemo<DisplayEntry[] | null>(() => {
    if (!tonality) {
      return null;
    }

    const list = diatonicChordsForKey(tonality.tonic, tonality.mode);

    if (list.length === 0) {
      return null;
    }

    return list.map((d) => ({ entry: d.entry, roman: d.roman }));
  }, [tonality]);

  const scoped = useMemo<DisplayEntry[]>(() => {
    if (showAll) {
      return FULL_CATALOG.map((entry) => ({ entry }));
    }

    if (diatonic) {
      return diatonic;
    }

    return DEFAULT_CATALOG.map((entry) => ({ entry }));
  }, [showAll, diatonic]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      return scoped;
    }

    return scoped.filter(({ entry: e }) => {
      return (
        e.symbol.toLowerCase().includes(q) ||
        e.root.toLowerCase().includes(q) ||
        e.noteNames.some((n) => n.toLowerCase().includes(q))
      );
    });
  }, [scoped, search]);

  const scopeLabel = useMemo(() => {
    if (showAll || !tonality) {
      return null;
    }

    return formatTonality(tonality);
  }, [showAll, tonality]);

  const toggleTargetCount = useMemo(() => {
    if (showAll) {
      return diatonic ? diatonic.length : DEFAULT_CATALOG.length;
    }

    return FULL_CATALOG.length;
  }, [showAll, diatonic]);

  const title = scopeLabel ? `Gamme · ${scopeLabel}` : "Accords";
  const description = scopeLabel
    ? "Les accords diatoniques de la gamme sélectionnée."
    : "Choisis un accord pour le pratiquer.";

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={{
        background: "var(--color-overlay-deep)",
        color: TEXT,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 80% 20%, ${VIOLET_SOFT} 0%, transparent 50%)`,
        }}
      />

      {/*
        Les tailles passent par des classes et non par le style inline du reste
        du fichier : c'est le seul moyen de les faire varier sans hook de media
        query, qui annoncerait la version mobile au premier rendu et ferait
        clignoter le desktop. Les valeurs par défaut reprennent exactement les
        anciennes valeurs inline.

        Pourquoi : sur un téléphone couché, ce calque ne fait que ~144 px (il
        est clampé au-dessus du clavier), alors que l'en-tête en réclamait
        ~162 — la liste se retrouvait à une hauteur de 0, poussée sous le bas
        du conteneur. On ne voyait donc aucun accord, et rien ne le laissait
        deviner puisque le conteneur, lui, ne déborde pas.

        Le titre et la recherche restent : ce sont le repère et la commande.
        Seule la description disparaît — c'est une annotation, et la règle du
        chantier est qu'on retire les annotations, jamais les commandes.
      */}
      <div className="relative flex flex-1 min-h-0 flex-col py-6 px-8 [@media(max-height:480px)]:py-2 [@media(max-height:480px)]:px-4">
        <header className="mb-[18px] [@media(max-height:480px)]:mb-1.5">
          <h1
            className="text-[22px] [@media(max-height:480px)]:text-[15px]"
            style={{
              fontWeight: 300,
              letterSpacing: "-0.02em",
              marginBottom: 4,
              color: TEXT,
            }}
          >
            {title}
          </h1>
          <p
            className="[@media(max-height:480px)]:hidden"
            style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5 }}
          >
            {description}
          </p>
        </header>

        <div
          className="mb-4 [@media(max-height:480px)]:mb-1.5"
          style={{ display: "flex", gap: 10 }}
        >
          <SearchInput value={search} onChange={setSearch} />
          <ToggleAllButton
            showAll={showAll}
            count={toggleTargetCount}
            onToggle={() => setShowAll(!showAll)}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <EmptyState />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 10,
              }}
            >
              {filtered.map(({ entry, roman }) => (
                <CardWithRoman
                  key={entry.id}
                  entry={entry}
                  roman={roman}
                  onClick={() => onSelect(entry)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTonality(tonality: Tonality): string {
  const tonic = noteLabel(tonality.tonic, "latin");
  const mode = tonality.mode === "major" ? "majeur" : "mineur";

  return `${tonic} ${mode}`;
}

type CardWithRomanProps = {
  entry: ChordEntry;
  roman?: string;
  onClick: () => void;
};

function CardWithRoman({ entry, roman, onClick }: CardWithRomanProps) {
  if (!roman) {
    return <ChordLibraryCard entry={entry} onClick={onClick} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: VIOLET_BRIGHT,
          letterSpacing: "0.12em",
          fontWeight: 600,
          paddingLeft: 4,
        }}
      >
        {roman}
      </span>
      <ChordLibraryCard entry={entry} onClick={onClick} />
    </div>
  );
}

type SearchInputProps = {
  value: string;
  onChange: (v: string) => void;
};

function SearchInput({ value, onChange }: SearchInputProps) {
  return (
    <div style={{ flex: 1, position: "relative" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Recherche : C, Em, Csus4..."
        style={{
          width: "100%",
          padding: "11px 14px 11px 36px",
          background: SURFACE_LO,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          color: TEXT,
          fontSize: 13,
          fontFamily: "inherit",
          outline: "none",
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: TEXT_MUTE,
          fontSize: 13,
        }}
      >
        ⌕
      </span>
    </div>
  );
}

type ToggleAllButtonProps = {
  showAll: boolean;
  count: number;
  onToggle: () => void;
};

function ToggleAllButton({ showAll, count, onToggle }: ToggleAllButtonProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        padding: "11px 16px",
        background: SURFACE_LO,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        fontSize: 11,
        color: TEXT_DIM,
        fontFamily: FONT_MONO,
        letterSpacing: "0.1em",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {showAll ? "ESSENTIELS" : "TOUT AFFICHER"}
      <span style={{ color: TEXT_MUTE }}>{count}</span>
    </button>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: "48px 0",
        textAlign: "center",
        border: `1px solid ${BORDER}`,
        background: SURFACE_LO,
        borderRadius: 10,
        color: TEXT_MUTE,
        fontSize: 13,
      }}
    >
      Aucun accord ne correspond.
    </div>
  );
}
